import { useEffect, useRef, useState } from 'react';

function isTouchLikeDevice() {
  if (typeof window === 'undefined') return false;
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    window.matchMedia('(pointer: coarse)').matches ||
    window.matchMedia('(hover: none)').matches
  );
}

export default function CustomCursor() {
  const dotRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef(0);
  const latestRef = useRef({ x: 0, y: 0, hovering: false });
  const [isTouchDevice, setIsTouchDevice] = useState(() => isTouchLikeDevice());

  useEffect(() => {
    const checkTouch = () => setIsTouchDevice(isTouchLikeDevice());

    checkTouch();
    window.addEventListener('resize', checkTouch, { passive: true });
    return () => window.removeEventListener('resize', checkTouch);
  }, []);

  useEffect(() => {
    document.body.classList.toggle('custom-cursor-active', !isTouchDevice);
    return () => {
      document.body.classList.remove('custom-cursor-active');
    };
  }, [isTouchDevice]);

  useEffect(() => {
    if (isTouchDevice) return;

    const render = () => {
      const { x, y, hovering } = latestRef.current;
      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${x - 8}px, ${y - 8}px, 0) scale(${hovering ? 2 : 1})`;
      }
      if (ringRef.current) {
        ringRef.current.style.transform = `translate3d(${x - 24}px, ${y - 24}px, 0) scale(${hovering ? 1.5 : 1})`;
        ringRef.current.style.opacity = hovering ? '0' : '1';
      }
      frameRef.current = 0;
    };

    const scheduleRender = () => {
      if (frameRef.current) return;
      frameRef.current = window.requestAnimationFrame(render);
    };

    const updateMousePosition = (e: MouseEvent) => {
      latestRef.current.x = e.clientX;
      latestRef.current.y = e.clientY;
      scheduleRender();
    };

    const handleMouseOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      latestRef.current.hovering = Boolean(
        target.closest('a, button, .interactive, [role="button"]'),
      );
      scheduleRender();
    };

    window.addEventListener('mousemove', updateMousePosition, { passive: true });
    window.addEventListener('mouseover', handleMouseOver, { passive: true });

    return () => {
      window.cancelAnimationFrame(frameRef.current);
      window.removeEventListener('mousemove', updateMousePosition);
      window.removeEventListener('mouseover', handleMouseOver);
    };
  }, [isTouchDevice]);

  if (isTouchDevice) return null;

  return (
    <>
      <div
        ref={dotRef}
        className="custom-cursor-element fixed top-0 left-0 w-4 h-4 bg-white rounded-full pointer-events-none z-[9999] mix-blend-difference transition-transform duration-75 ease-out will-change-transform"
      />
      <div
        ref={ringRef}
        className="custom-cursor-element fixed top-0 left-0 w-12 h-12 border border-white/30 rounded-full pointer-events-none z-[9998] transition-[opacity,transform] duration-150 ease-out will-change-transform"
      />
    </>
  );
}